import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Input, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import useVehicleTypeService from "../../../services/backoffice/hooks/useVehicleTypeService";
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import VehicleTypeForm from "./create-vehicle-type-form";
import {VehicleTypeFilters} from "../../../filters/vehicle-type-filters";
import {VehicleType} from "../../../models/fleet-type";

interface VehicleTypesListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const VehicleTypesList: React.FC<VehicleTypesListProps> = ({ isModal = false, open = false, onClose }) => {
    const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<VehicleTypeFilters>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getVehicleTypes, deleteVehicleType, activateVehicleType, deactivateVehicleType } = useVehicleTypeService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const [showFilters, setShowFilters] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editVehicleTypeId, setEditVehicleTypeId] = useState<string | null>(null);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadVehicleTypes() {
            setLoading(true);
            try {
                const response = await getVehicleTypes(filter);
                setVehicleTypes(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar tipos de veículo.");
            } finally {
                setLoading(false);
            }
        }
        loadVehicleTypes();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir este tipo de veículo?");
            if (result) {
                await deleteVehicleType(id);
                toast.success("Tipo de veículo excluído com sucesso", ToasterConfig.success);
                setVehicleTypes(vehicleTypes.filter((vehicle) => vehicle.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir tipo de veículo", ToasterConfig.error);
        }
    };

    const handleActivation = async (vehicleType: VehicleType) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${vehicleType.isActive ? 'desativar' : 'ativar'} este tipo de veículo?`);
            if (result) {
                if (vehicleType.isActive) {
                    await deactivateVehicleType(vehicleType.id);
                } else {
                    await activateVehicleType(vehicleType.id);
                }
                setFilter({ ...filter });
                toast.success(`Tipo de veículo ${vehicleType.isActive ? 'desativado' : 'ativado'} com sucesso.`, ToasterConfig.success);
            }
        } catch (err: any) {
            toast.error("Erro ao alterar status do tipo de veículo", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditVehicleTypeId(id);
        setModalOpen(true);
    };

    const formContent = (
        <Segment raised style={{ maxWidth: "900px", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Tipos de Veículo
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={() => setModalOpen(true)}>
                            <Icon name='plus' /> Novo
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label htmlFor="code">Código</label>
                                <Input
                                    fluid
                                    id="code"
                                    icon="search"
                                    type="text"
                                    placeholder="Código de tipo de veículo"
                                    value={filter?.code ?? ''}
                                    onChange={(event) => setFilter({ ...filter, code: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <label htmlFor="name">Tipo de Veículo</label>
                                <Input
                                    fluid
                                    id="name"
                                    icon="search"
                                    type="text"
                                    placeholder="Tipo de veículo"
                                    value={filter?.name ?? ''}
                                    onChange={(event) => setFilter({ ...filter, name: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color="green">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Código</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {vehicleTypes?.map((vehicleType) => (
                        <Table.Row key={vehicleType.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar tipo de veículo"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => {
                                                        setEditVehicleTypeId(vehicleType.id);
                                                        setModalOpen(true);
                                                    }}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir tipo de veículo"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(vehicleType.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={vehicleType.isActive ? "Desativar tipo de veículo" : "Ativar tipo de veículo"}
                                            trigger={
                                                <Button
                                                    color={vehicleType.isActive ? 'red' : 'green'}
                                                    onClick={() => handleActivation(vehicleType)}
                                                    icon={vehicleType.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{vehicleType.code}</Table.Cell>
                            <Table.Cell>{vehicleType.name}</Table.Cell>
                            <Table.Cell>
                                <Label color={vehicleType.isActive ? 'green' : 'red'}>
                                    {vehicleType.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                totalPages={totalPages}
                defaultActivePage={filter?.Page ?? 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="tiny"
                style={{ width: '620px', maxWidth: '95vw', padding: '20px' }}
            >
                <VehicleTypeForm
                    isModal
                    open={modalOpen}
                    vehicleTypeId={editVehicleTypeId || undefined}
                    onClose={() => {
                        setModalOpen(false);
                        setEditVehicleTypeId(null);
                        setFilter({ ...filter });
                    }}
                />
            </Modal>
            {formContent}
        </>
    );
};

export default VehicleTypesList;
